import React, { useMemo, useState } from 'react';
import {
  Tree,
  Empty,
  Switch,
  Button,
  Modal,
  Alert,
  Tag,
  Row,
  Col,
  Select,
  Grid,
} from 'antd';
import {
  ButtonContainer,
  StyledLabel,
  SubmitButton,
} from '../../../../../../common/form/FormElements.jsx';
import PropTypes from 'prop-types';
import {
  CollapseBlock,
  SortApplicationRenderer,
} from '../../../../../../common/index';
import { useTranslation } from 'react-i18next';
import {
  useSaveErasmusApplicationMutation,
  useSaveErasmusApplicationStatusMutation,
} from '../../../../../../../redux/services/erasmusApplications';
import { useToastMessages } from '../../../../../../../hooks/useToastMessages';
// import EvaluationFunctionsList from '../../../../../../common/EvaluationFunction/EvaluationFunctionsList';
// import EvaluationFunctionEditor from '../../../../../../common/EvaluationFunction/EvaluationFunctionEditor';

const SEPARATOR_KEY = 'separator';
const STATUS_ACCEPTED = 'Akceptovaná';
const STATUS_REJECTED = 'Zamietnutá';

// vizuál
const makeRowStyle = (theme) => ({
  // position: 'relative',
  // display: 'block',
  // width: '100%',
  background: `linear-gradient(180deg, ${theme.bg1}, ${theme.bg2})`,
});
const GREEN_THEME = {
  bg1: 'rgba(246,255,237,0.95)',
  bg2: 'rgba(237,255,219,0.92)',
  border: '#95de64',
  glow: 'rgba(82,196,26,0.10)',
  accent: '#52c41a',
};
const RED_THEME = {
  bg1: 'rgba(255,242,240,0.95)',
  bg2: 'rgba(255,231,230,0.92)',
  border: '#ffa39e',
  glow: 'rgba(255,77,79,0.10)',
  accent: '#ff4d4f',
};


const SeparatorBar = ({ label }) => (
  <div
    role="separator"
    style={{
      width: '100%',
      margin: '8px 0px 8px 0px',
      padding: '8px 12px',
      background:
        'linear-gradient(90deg, rgba(230,247,255,0.9), rgba(240,245,255,0.9))',
      border: '2px dashed #69b1ff',
      borderRadius: 10,
      display: 'flex',
      alignItems: 'center',
      gap: 10,
      cursor: 'grab',
      boxShadow:
        '0 1px 0 rgba(0,0,0,0.04), inset 0 0 0 1px rgba(105,177,255,0.15)',
      userSelect: 'none',
    }}
    title={label}
  >
    <span style={{ opacity: 0.55, fontSize: 18, lineHeight: 1 }}>⋮⋮</span>
    <div
      style={{
        flex: 1,
        textAlign: 'center',
        fontWeight: 600,
        color: '#0958d9',
        letterSpacing: 0.2,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 8,
      }}
    >
      <span style={{ opacity: 0.7 }}>↑</span>
      <span>{label}</span>
      <span style={{ opacity: 0.7 }}>↓</span>
    </div>
  </div>
);

// helpery
const ensureSeparator = (list) => {
  const safe = Array.isArray(list) ? list : [];
  const has = safe.some((n) => n?.key === SEPARATOR_KEY);
  if (has) return safe;
  const mid = Math.ceil(safe.length / 2);
  return [
    ...safe.slice(0, mid),
    { key: SEPARATOR_KEY, isSeparator: true, position: null },
    ...safe.slice(mid),
  ];
};
const arrayMove = (arr, from, to) => {
  const next = arr.slice();
  const [item] = next.splice(from, 1);
  next.splice(to, 0, item);
  return next;
};
const getStudyProgramId = (sp) => sp?.id ?? sp?.['id'] ?? sp;

// ---- hlavný komponent ----
const ErazmusApplicationSortOrder = ({
  applicationsSort,
  setApplicationsSort,
  onOrderSave, // legacy
  mobilityTypesOptions,
  mobilitySubypesOptions,
  challengeOptions,
  onTypeChange,
  onSubTypeChange,
  onChallengeChange,
  type,
  subType,
  challenge,
  // onRefreshApplications, // passni refetch z rodiča
}) => {
  const { t } = useTranslation();
  const screens = Grid.useBreakpoint();
  const { saveSuccess, warningMessage } = useToastMessages();

  const [saveErasmusApplication, { isLoading: isSavingApp }] =
    useSaveErasmusApplicationMutation();
  const [saveErasmusApplicationStatus, { isLoading: isSavingStatus }] =
    useSaveErasmusApplicationStatusMutation();

  const [evaluationEnabled, setEvaluationEnabled] = useState(false);
  const [isEvaluating, setIsEvaluating] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  // const [editingFunctionId, setEditingFunctionId] = useState(null);

  const hasSelectedFilter =
    (type !== null && type !== undefined) ||
    (subType !== null && subType !== undefined) ||
    (challenge !== null && challenge !== undefined);

  // const currentIdsForEvaluation = () => {
  //   const list =
  //     dataForRender?.filter(
  //       (n) => !n?.isSeparator && n?.key !== SEPARATOR_KEY,
  //     ) || [];
  //   return list.map((n) => n.id);
  // };

  // render dáta (so/bez separátora)
  const dataForRender = useMemo(() => {
    const base = applicationsSort ?? [];
    return evaluationEnabled
      ? ensureSeparator(base)
      : base.filter((n) => n?.key !== SEPARATOR_KEY);
  }, [applicationsSort, evaluationEnabled]);

  const sepIndex = useMemo(
    () => dataForRender.findIndex((n) => n.key === SEPARATOR_KEY),
    [dataForRender],
  );

  const indexByKey = useMemo(() => {
    const m = new Map();
    dataForRender.forEach((n, i) => m.set(n.key, i));
    return m;
  }, [dataForRender]);

  // DnD handler
  const handleDrop = (info) => {
    const { dragNode, node, dropPosition } = info;

    const list = dataForRender;
    const from = list.findIndex((i) => i.key === dragNode.key);
    const target = list.findIndex((i) => i.key === node.key);
    if (from === -1 || target === -1) return;

    const toRaw = dropPosition < 0 ? target : target + 1;
    const to = toRaw > from ? toRaw - 1 : toRaw;
    if (from === to) return;

    const moved = arrayMove(list, from, to);

    let pos = 1;
    const newData = moved.map((item) =>
      item.key === SEPARATOR_KEY
        ? { ...item, position: null }
        : { ...item, position: pos++ },
    );

    setApplicationsSort(newData);
  };

  const getOrderedApplications = () => {
    let position = 1;

    return dataForRender
      .filter((item) => !item?.isSeparator && item?.key !== SEPARATOR_KEY)
      .map((item) => ({
        ...item,
        position: position++,
      }));
  };

  const handleOrderSave = async () => {
    if (!hasSelectedFilter) {
      return warningMessage({
        message: t('Najprv vyberte filter prihlášok.'),
      });
    }

    const orderedApplications = getOrderedApplications();

    if (!orderedApplications.length) {
      return warningMessage({
        message: t('Žiadne prihlášky na uloženie.'),
      });
    }

    if (typeof onOrderSave !== 'function') {
      return warningMessage({
        message: t('Ukladanie poradia nie je dostupné.'),
      });
    }

    await onOrderSave(orderedApplications);
  };

  // počty nad/pod hranicou + zoznam bez separátora
  const getCounts = () => {
    if (!Array.isArray(dataForRender) || sepIndex === -1)
      return { above: [], below: [], ordered: [] };
    const ordered = dataForRender.filter(
      (x) => !x?.isSeparator && x?.key !== SEPARATOR_KEY,
    );
    const above = ordered.filter((_, i) => i < sepIndex);
    const below = ordered.filter((_, i) => i >= sepIndex);
    return { above, below, ordered };
  };

  // manuálne vyhodnotenie so statusmi (tvoj pôvodný proces)
  const handleEvaluateExec = async () => {
    const { ordered } = getCounts();

    if (!evaluationEnabled) {
      return warningMessage({ message: t('Zapnite najprv vyhodnotenie.') });
    }
    if (!Array.isArray(dataForRender) || dataForRender.length === 0) {
      return warningMessage({ message: t('Žiadne dáta na vyhodnotenie.') });
    }
    if (sepIndex === -1) {
      return warningMessage({
        message: t('Nie je umiestnená hranica vyhodnotenia (separator).'),
      });
    }
    if (ordered.length === 0) {
      return warningMessage({
        message: t('Po odfiltrovaní separátora nezostal žiadny záznam.'),
      });
    }

    let ok = 0;
    setIsEvaluating(true);
    try {
      for (let i = 0; i < ordered.length; i++) {
        const node = ordered[i];
        const isAbove = i < sepIndex;
        const statusValue = isAbove ? STATUS_ACCEPTED : STATUS_REJECTED;
        const note = isAbove
          ? t('Automatické vyhodnotenie: nad hranicou')
          : t('Automatické vyhodnotenie: pod hranicou');

        const studyProgramId = getStudyProgramId(node?.study_program);

        // 1) update aplikácie
        await saveErasmusApplication({
          body: {
            ...node,
            applicationStatus: statusValue,
            study_program: studyProgramId,
          },
          id: node.id,
        }).unwrap();

        // 2) log zmeny statusu
        await saveErasmusApplicationStatus({
          body: {
            application_id: node.id,
            applicationStatus: statusValue,
            note,
          },
        }).unwrap();

        ok++;
      }

      saveSuccess({
        message: t('Hotovo: {{n}} záznamov vyhodnotených.', { n: ok }),
      });
    } catch (e) {
      console.error('[evaluate] UNCAUGHT:', e);
      warningMessage({
        message: t('Chyba pri vyhodnocovaní. Podrobnosti v konzole.'),
      });
    } finally {
      setIsEvaluating(false);
    }
  };

  const isBtnDisabled =
    !evaluationEnabled || isEvaluating || isSavingApp || isSavingStatus;

  const { above, below } = getCounts();

  return (
    <div style={{ margin: '10px' }}>
      {/* Filtre */}
      <Row
        justify="center"
        gutter={[24, 16]}
        style={{
          maxWidth: '1000px',
          margin: '0 auto',
          padding: '0 8px',
        }}
      >
        <Col xs={24} md={8}>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: screens.md ? '110px minmax(0, 1fr)' : '1fr',
              alignItems: 'center',
              maxWidth: '310px',
              margin: '0 auto',
              gap: screens.md ? '12px' : '6px',
            }}
          >
            <span style={{ fontWeight: 600, textAlign: screens.md ? 'right' : 'left' }}>
              {t('Typ')}
            </span>
            <Select
              placeholder={t('Vyberte typ mobility')}
              options={mobilityTypesOptions}
              style={{ width: '100%' }}
              onChange={onTypeChange}
              value={type}
              allowClear
              showSearch
              filterOption={(input, option) =>
                String(option?.label ?? '').toLowerCase().includes(input.toLowerCase())
              }
            />
          </div>
        </Col>
        <Col xs={24} md={8}>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: screens.md ? '110px minmax(0, 1fr)' : '1fr',
              alignItems: 'center',
              maxWidth: '310px',
              margin: '0 auto',
              gap: screens.md ? '12px' : '6px',
            }}
          >
            <span
              style={{
                fontWeight: 600,
                textAlign: screens.md ? 'right' : 'left',
              }}
            >
              {t('Podtyp')}
            </span>
            <Select
              placeholder={t('Vyberte podtyp mobility')}
              options={mobilitySubypesOptions}
              style={{ width: '100%' }}
              onChange={onSubTypeChange}
              value={subType}
              allowClear
              showSearch
              filterOption={(input, option) =>
                String(option?.label ?? '').toLowerCase().includes(input.toLowerCase())
              }
            />
          </div>
        </Col>
        <Col xs={24} md={8}>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: screens.md ? '110px minmax(0, 1fr)' : '1fr',
              alignItems: 'center',
              maxWidth: '310px',
              margin: '0 auto',
              gap: screens.md ? '12px' : '6px',
            }}
          >
            <span style={{ fontWeight: 600, textAlign: screens.md ? 'right' : 'left' }}>
              {t('Výzva')}
            </span>
            <Select
              placeholder={t('Vyberte výzvu')}
              options={challengeOptions}
              style={{ width: '100%' }}
              onChange={onChallengeChange}
              value={challenge}
              allowClear
              showSearch
              filterOption={(input, option) =>
                String(option?.label ?? '').toLowerCase().includes(input.toLowerCase())
              }
              popupMatchSelectWidth={screens.md ? 420 : true}
            />
          </div>
        </Col>
      </Row>
      <div>
        <Alert
          type="info"
          showIcon
          title={
            <>
              {t('Zobrazia sa len prihlášky ktoré sú v stave')}{' '}
              <Tag color="yellow" style={{ marginInlineEnd: 0 }} >{t('Odoslaná')}</Tag>
            </>
          }
          style={{ marginTop: 12, marginBottom: 12 }}
        />
      </div>

      {/*
      <CollapseBlock
        collapsible={true}
        title={t('Vyhodnocovacia funkcia (koeficient)')}
        closedByDefault={true}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 12,
          }}
        >
          <div />
          <Button type="dashed" onClick={() => setEditingFunctionId(null)}>
            + {t('Nová funkcia')}
          </Button>
        </div>

        <div style={{ marginBottom: 12 }}>
          <EvaluationFunctionsList
            onEdit={setEditingFunctionId}
            selectedIds={currentIdsForEvaluation()}
            onAfterRun={() => {
              onRefreshApplications && onRefreshApplications();
              saveSuccess({
                message: t('Prepočet hotový. Koeficienty/pozície uložené.'),
              });
            }}
          />
        </div>

        <EvaluationFunctionEditor
          functionId={editingFunctionId}
          selectedIds={currentIdsForEvaluation()}
          onAfterRun={() => {
            onRefreshApplications && onRefreshApplications();
            saveSuccess({
              message: t('Prepočet hotový. Koeficienty/pozície uložené.'),
            });
          }}
          onSaved={(createdId) => {
            if (createdId) setEditingFunctionId(createdId);
          }}
        />
      </CollapseBlock>
      */}

      {/* manuálne vyhodnotenie + spúšťací button */}
      <CollapseBlock
        collapsible={true}
        title={t('Vyhodnotenie prihlášok')}
        closedByDefault={true}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            justifyContent: 'space-between',
            padding: '4px 0',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontWeight: 500 }}>{t('Povoliť vyhodnotenie')}</span>
            <Switch
              checked={evaluationEnabled}
              onChange={setEvaluationEnabled}
              disabled={isEvaluating}
            />
          </div>

          <Button
            type="primary"
            loading={isEvaluating}
            disabled={isBtnDisabled}
            onClick={() => {
              if (!evaluationEnabled)
                return warningMessage({
                  message: t('Zapnite najprv vyhodnotenie.'),
                });
              if (sepIndex === -1)
                return warningMessage({
                  message: t(
                    'Nie je umiestnená hranica vyhodnotenia (separator).',
                  ),
                });
              const { ordered } = getCounts();
              if (!ordered.length)
                return warningMessage({
                  message: t(
                    'Po odfiltrovaní separátora nezostal žiadny záznam.',
                  ),
                });
              setConfirmOpen(true);
            }}
          >
            {isEvaluating ? t('Vyhodnocujem…') : t('Spustiť vyhodnotenie')}
          </Button>
        </div>
      </CollapseBlock>

      {/* Strom s DnD a vizuálnou hranicou */}
      <CollapseBlock>
        <StyledLabel>{t('Upraviť poradie prihlášok')}</StyledLabel>

        {!hasSelectedFilter ? (
          <Empty
            description={t(
              'Vyberte výzvu, typ alebo podtyp mobility pre zobrazenie prihlášok.',
            )}
          />
        ) : dataForRender && dataForRender.length > 0 ? (
          <Tree
            className="draggable-tree"
            draggable={{ icon: false, nodeDraggable: () => true }}
            allowDrop={({ dropPosition }) => dropPosition !== 0}
            blockNode
            onDrop={handleDrop}
            treeData={dataForRender}
            titleRender={(node) => {
              if (node.isSeparator) {
                return <SeparatorBar label={t('Hranica vyhodnotenia')} />;
              }

              let rowStyle = {};
              let accentColor = undefined;

              if (evaluationEnabled && sepIndex !== -1) {
                const myIndex = indexByKey.get(node.key);
                if (typeof myIndex === 'number') {
                  const isAbove = myIndex < sepIndex;
                  const theme = isAbove ? GREEN_THEME : RED_THEME;
                  rowStyle = makeRowStyle(theme);
                  accentColor = theme.accent;
                }
              }

              return (
                <SortApplicationRenderer
                  rowStyle={rowStyle}
                  accentColor={accentColor}
                  position={node.position}
                  firstName={node.person?.first_name}
                  lastName={node.person?.last_name}
                  schoolType={node.university1?.name}
                  score={node.coefficient}
                />
              );
            }}
          />
        ) : (
          <Empty description={t('Žiadne prihlášky na zobrazenie')} />
        )}
      </CollapseBlock>

      <ButtonContainer>
        <SubmitButton
          onClick={handleOrderSave}
          type="button"
          isDisabled={!hasSelectedFilter || isEvaluating || isSavingApp}
        >
          {t('Uložiť')}
        </SubmitButton>
      </ButtonContainer>

      {/* Potvrdzovací MODAL */}
      <Modal
        visible={confirmOpen}
        title={t('Spustiť vyhodnotenie?')}
        onCancel={() => setConfirmOpen(false)}
        onOk={() => {
          setConfirmOpen(false);
          handleEvaluateExec();
        }}
        width={520}
        destroyOnHidden
      >
        <div>
          <div>
            {t('Akceptovaných:')} <b>{above.length}</b>
          </div>
          <div>
            {t('Zamietnutých:')} <b>{below.length}</b>
          </div>
        </div>
      </Modal>
    </div>
  );
};

ErazmusApplicationSortOrder.propTypes = {
  applicationsSort: PropTypes.array,
  setApplicationsSort: PropTypes.func,
  onOrderSave: PropTypes.func,
  mobilityTypesOptions: PropTypes.array,
  mobilitySubypesOptions: PropTypes.array,
  challengeOptions: PropTypes.array,
  onTypeChange: PropTypes.func,
  onSubTypeChange: PropTypes.func,
  onChallengeChange: PropTypes.func,
  type: PropTypes.any,
  subType: PropTypes.any,
  challenge: PropTypes.any,
  // onRefreshApplications: PropTypes.func,
};

export default ErazmusApplicationSortOrder;
